/**
 * Tabs Component
 *
 * Accessible tab interface following WAI-ARIA patterns.
 * Supports horizontal/vertical orientation, icons, badges, and controlled/uncontrolled modes.
 *
 * Usage:
 * <Tabs defaultValue="tab1">
 *   <TabList>
 *     <Tab value="tab1">Overview</Tab>
 *     <Tab value="tab2">Details</Tab>
 *   </TabList>
 *   <TabPanels>
 *     <TabPanel value="tab1">Overview content</TabPanel>
 *     <TabPanel value="tab2">Details content</TabPanel>
 *   </TabPanels>
 * </Tabs>
 */

import React, { createContext, useContext, useState, useRef, useCallback, useId } from 'react';
import PropTypes from 'prop-types';

// ========================================
// Context
// ========================================

const TabsContext = createContext(null);

const useTabsContext = () => {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('Tab components must be used within a Tabs provider');
  }
  return context;
};

// ========================================
// Tabs Container
// ========================================

const Tabs = ({
  children,
  defaultValue,
  value: controlledValue,
  onChange,
  orientation = 'horizontal',
  variant = 'default',
  size = 'md',
  fullWidth = false,
  className = '',
}) => {
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
  const baseId = useId();

  const isControlled = controlledValue !== undefined;
  const activeValue = isControlled ? controlledValue : uncontrolledValue;

  const handleChange = useCallback((newValue) => {
    if (!isControlled) {
      setUncontrolledValue(newValue);
    }
    onChange?.(newValue);
  }, [isControlled, onChange]);

  const contextValue = {
    activeValue,
    onChange: handleChange,
    orientation,
    variant,
    size,
    fullWidth,
    baseId,
  };

  return (
    <TabsContext.Provider value={contextValue}>
      <div
        className={`${orientation === 'vertical' ? 'flex gap-4' : ''} ${className}`}
        data-orientation={orientation}
      >
        {children}
      </div>
    </TabsContext.Provider>
  );
};

Tabs.propTypes = {
  children: PropTypes.node.isRequired,
  defaultValue: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  orientation: PropTypes.oneOf(['horizontal', 'vertical']),
  variant: PropTypes.oneOf(['default', 'pills', 'underline', 'enclosed']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  fullWidth: PropTypes.bool,
  className: PropTypes.string,
};

// ========================================
// Tab List
// ========================================

const TabList = ({ children, className = '', 'aria-label': ariaLabel }) => {
  const { orientation, variant, fullWidth } = useTabsContext();
  const tabListRef = useRef(null);

  const handleKeyDown = (event) => {
    const tabs = tabListRef.current?.querySelectorAll('[role="tab"]:not([disabled])');
    if (!tabs?.length) return;

    const tabsArray = Array.from(tabs);
    const currentIndex = tabsArray.findIndex(tab => tab === document.activeElement);

    let nextIndex;

    const isHorizontal = orientation === 'horizontal';
    const prevKey = isHorizontal ? 'ArrowLeft' : 'ArrowUp';
    const nextKey = isHorizontal ? 'ArrowRight' : 'ArrowDown';

    switch (event.key) {
      case prevKey:
        event.preventDefault();
        nextIndex = currentIndex > 0 ? currentIndex - 1 : tabsArray.length - 1;
        break;
      case nextKey:
        event.preventDefault();
        nextIndex = currentIndex < tabsArray.length - 1 ? currentIndex + 1 : 0;
        break;
      case 'Home':
        event.preventDefault();
        nextIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        nextIndex = tabsArray.length - 1;
        break;
      default:
        return;
    }

    tabsArray[nextIndex]?.focus();
  };

  const variantStyles = {
    default: 'border-b border-white/10',
    pills: 'bg-white/5 p-1 rounded-lg',
    underline: 'border-b-2 border-white/10',
    enclosed: 'border border-white/10 rounded-t-lg bg-white/5',
  };

  const orientationStyles = {
    horizontal: 'flex flex-row',
    vertical: 'flex flex-col min-w-[200px]',
  };

  return (
    <div
      ref={tabListRef}
      role="tablist"
      aria-label={ariaLabel}
      aria-orientation={orientation}
      onKeyDown={handleKeyDown}
      className={`
        ${orientationStyles[orientation]}
        ${variantStyles[variant]}
        ${fullWidth ? 'w-full' : 'w-fit'}
        ${className}
      `}
    >
      {children}
    </div>
  );
};

TabList.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  'aria-label': PropTypes.string,
};

// ========================================
// Tab
// ========================================

const Tab = ({
  children,
  value,
  disabled = false,
  icon,
  badge,
  className = '',
}) => {
  const { activeValue, onChange, variant, size, fullWidth, baseId } = useTabsContext();
  const isActive = activeValue === value;

  const sizeStyles = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-5 py-2.5 text-base',
  };

  const variantStyles = {
    default: isActive
      ? 'text-white border-b-2 border-portfolio-purple -mb-px'
      : 'text-gray-400 hover:text-white border-b-2 border-transparent',
    pills: isActive
      ? 'bg-portfolio-purple text-white rounded-md'
      : 'text-gray-400 hover:text-white hover:bg-white/10 rounded-md',
    underline: isActive
      ? 'text-portfolio-purple border-b-2 border-portfolio-purple -mb-0.5'
      : 'text-gray-400 hover:text-white border-b-2 border-transparent',
    enclosed: isActive
      ? 'bg-gray-900 text-white border-b-0 rounded-t-lg -mb-px border border-white/10 border-b-gray-900'
      : 'text-gray-400 hover:text-white bg-transparent',
  };

  const handleClick = () => {
    if (!disabled) {
      onChange(value);
    }
  };

  return (
    <button
      role="tab"
      id={`${baseId}-tab-${value}`}
      aria-selected={isActive}
      aria-controls={`${baseId}-panel-${value}`}
      tabIndex={isActive ? 0 : -1}
      disabled={disabled}
      onClick={handleClick}
      className={`
        ${sizeStyles[size]}
        ${variantStyles[variant]}
        ${fullWidth ? 'flex-1' : ''}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        font-medium transition-all duration-200
        focus:outline-none focus:ring-2 focus:ring-portfolio-purple/50 focus:ring-offset-2
        focus:ring-offset-gray-900
        inline-flex items-center justify-center gap-2
        whitespace-nowrap
        ${className}
      `}
    >
      {icon && <span className="w-4 h-4">{icon}</span>}
      {children}
      {badge !== undefined && (
        <span className={`
          ml-1.5 px-1.5 py-0.5 text-xs font-medium rounded-full
          ${isActive ? 'bg-white/20' : 'bg-white/10'}
        `}>
          {badge}
        </span>
      )}
    </button>
  );
};

Tab.propTypes = {
  children: PropTypes.node.isRequired,
  value: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  icon: PropTypes.node,
  badge: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
};

// ========================================
// Tab Panels Container
// ========================================

const TabPanels = ({ children, className = '' }) => {
  return (
    <div className={`mt-4 ${className}`}>
      {children}
    </div>
  );
};

TabPanels.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

// ========================================
// Tab Panel
// ========================================

const TabPanel = ({ children, value, className = '' }) => {
  const { activeValue, baseId } = useTabsContext();
  const isActive = activeValue === value;

  if (!isActive) return null;

  return (
    <div
      role="tabpanel"
      id={`${baseId}-panel-${value}`}
      aria-labelledby={`${baseId}-tab-${value}`}
      tabIndex={0}
      className={`
        focus:outline-none focus:ring-2 focus:ring-portfolio-purple/50
        focus:ring-offset-2 focus:ring-offset-gray-900 rounded-lg
        ${className}
      `}
    >
      {children}
    </div>
  );
};

TabPanel.propTypes = {
  children: PropTypes.node.isRequired,
  value: PropTypes.string.isRequired,
  className: PropTypes.string,
};

// ========================================
// Convenience Component: Simple Tabs
// ========================================

/**
 * SimpleTabs - Quick tab interface from array of items
 *
 * Usage:
 * <SimpleTabs
 *   items={[
 *     { label: 'Overview', content: <Overview /> },
 *     { label: 'Details', content: <Details /> },
 *   ]}
 * />
 */
export const SimpleTabs = ({
  items,
  defaultIndex = 0,
  variant = 'default',
  size = 'md',
  className = '',
}) => {
  const defaultValue = items[defaultIndex]?.label || items[0]?.label;

  return (
    <Tabs defaultValue={defaultValue} variant={variant} size={size} className={className}>
      <TabList aria-label="Content tabs">
        {items.map((item) => (
          <Tab
            key={item.label}
            value={item.label}
            disabled={item.disabled}
            icon={item.icon}
            badge={item.badge}
          >
            {item.label}
          </Tab>
        ))}
      </TabList>
      <TabPanels>
        {items.map((item) => (
          <TabPanel key={item.label} value={item.label}>
            {item.content}
          </TabPanel>
        ))}
      </TabPanels>
    </Tabs>
  );
};

SimpleTabs.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    content: PropTypes.node.isRequired,
    disabled: PropTypes.bool,
    icon: PropTypes.node,
    badge: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  })).isRequired,
  defaultIndex: PropTypes.number,
  variant: PropTypes.oneOf(['default', 'pills', 'underline', 'enclosed']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string,
};

// ========================================
// Hook: useTabState
// ========================================

/**
 * Hook for managing tab state externally
 */
export const useTabState = (defaultValue) => {
  const [value, setValue] = useState(defaultValue);

  return {
    value,
    onChange: setValue,
    isActive: (tabValue) => value === tabValue,
  };
};

// ========================================
// Exports
// ========================================

export { TabList, Tab, TabPanels, TabPanel };
export default Tabs;
